<template>
  <base-data-table
    :service-provider="serviceProvider"
    :vuex-module="vuexModule"
    :header-columns="filteredHeaderColumns"
    :query-params="queryParams"
    label="Users"
  >
    <template #header-right>
      <div class="navbar-search form-inline ml-auto mr-3">
        <b-form-group class="mb-0">
          <b-input-group class="input-group-alternative input-group-merge">
            <div class="input-group-prepend">
              <span class="input-group-text"><i class="fas fa-search"></i></span>
            </div>
            <b-form-input @input="onSearch" placeholder="Search" size="sm" type="text">
            </b-form-input>
          </b-input-group>
        </b-form-group>
      </div>

      <base-button
        @click.stop="
          $bus.$emit('toggle-modal', {
            key: 'add-user',
            active: true,
          })
        "
        type="primary"
        size="sm"
        class="px-4"
        outline
      >
        <b-icon icon="person-plus" class="mr-1"></b-icon>
        Add User
      </base-button>
    </template>

    <template v-slot:cell_user="{ value, label }">
      <template v-if="'Name' === label"
        >{{ value.is_superuser ? "*" : "" }} {{ value.first_name }}</template
      >
      <template v-else-if="'Date Joined' === label">
        <template v-if="value.date_joined">{{
          value.date_joined | moment("timezone", "UTC", "MMM DD, YYYY")
        }}</template>
        <template v-else>N/A</template>
      </template>
      <template v-else-if="'Last Login' === label">
        <template v-if="value.last_login">{{
          value.last_login | moment("timezone", "UTC", "MMM DD, YYYY")
        }}</template>
        <template v-else>N/A</template>
      </template>
    </template>
    <template v-slot:cell_type="{ value }">{{
      value.replace("Corporate", "Company")
    }}</template>
    <template v-slot:cell_user_email="{ value }">
      <a :href="`mailto:${value}`">{{ value }}</a>
    </template>
    <template v-slot:cell_cash_call_approver="{ value, row }">
      <base-switch
        :id="`cash-call-approver-${row.id}`"
        :value="'Active' == value"
        onText=""
        offText=""
        @click.prevent="() => handleCashCallApproverStatusToggle(row)"
      ></base-switch>
    </template>
    <template v-slot:cell_status="{ value, row }">
      <template v-if="'Superuser' !== $user.get().role">
        {{ value ? "Active" : "Inactive" }}
      </template>
      <base-switch
        v-else
        :id="`status-${row.id}`"
        :value="'Active' == value"
        onText=""
        offText=""
        @click.prevent="() => handleStatusToggle(row)"
      ></base-switch>
    </template>
    <template v-slot:cell_billing_contact="{ value, row }">
      <base-switch
        :id="`billing-contact-${row.id}`"
        :value="'Active' == value"
        onText=""
        offText=""
        @click.prevent="() => handleBillingContactToggle(row)"
      ></base-switch>
    </template>
    <template v-slot:cell_user_rec_status="{ value, row }">
      <base-switch
        :id="`rec-status-${row.id}`"
        :value="'Active' == value"
        onText=""
        offText=""
        @click.prevent="() => handleRecStatusToggle(row)"
      ></base-switch>
    </template>
  </base-data-table>
</template>

<script>
import Vue from "vue";
import { mapMutations } from "vuex";

import { debounce } from "lodash-es";
import { companyUsersTableData } from "@datas";

import Members from "@services/Members";

import BaseDataTable from "@components/BaseDataTable";

export default {
  name: "table-company-users",
  components: {
    BaseDataTable,
  },
  inject: ["companySvc", "memberSvc"],
  data() {
    return {
      ...companyUsersTableData,
    };
  },
  computed: {
    filteredHeaderColumns() {
      const company = this.companySvc.company
      const showCashCallApprover = !!(company && company.tpa_internal_approval_required)
      const showBillingContact = !!(company && company.role && company.role.role === 'TPA')
      const showRecStatus = !!(company && company.rec_enabled)

      return this.headerColumns.filter(col => {
        if (col.prop === 'cash_call_approver') return showCashCallApprover
        if (col.prop === 'billing_contact') return showBillingContact
        if (col.prop === 'user_rec_status') return showRecStatus
        return true
      })
    }
  },
  methods: {
    ...mapMutations({
      mutateMember: "UPDATE_MEMBER",
    }),
    onSearch: debounce(function (keyword) {
      this.$bus.$emit("datatable-search", keyword);
    }),
    async handleStatusToggle(instance) {
      const id = instance.id;
      const type = "Company Admin" == instance.type ? "Corporate Admin" : "Employee";

      Vue.prototype.$swal({
        title: "Confirmation",
        text: `Are you sure you want to ${
          "Active" == instance.status ? "deactivate" : "activate"
        } this ${type}?`,
        type: "warning",
        confirmButtonText: "Okay",
        preConfirm: async (confirm) => {
          if (!confirm) return false;

          const resp = await Members.toggleMemberStatus(id, "Active" !== instance.status);
          const data = resp.data.data.data;

          this.mutateMember({
            data: data,
          });

          return true;
        },
      });
    },
    async handleBillingContactToggle(instance) {
      const name = instance.user.first_name

      Vue.prototype.$swal({
        title: 'Confirmation',
        text: `Are you sure you want to ${
          'Active' == instance.billing_contact ? 'remove' : 'designate'
        } ${name} as a Billing Contact?`,
        type: 'warning',
        confirmButtonText: 'Okay',
        preConfirm: async (confirm) => {
          if (!confirm) return false

          const resp = await Members.toggleBillingContactStatus(
            instance.id,
            'Active' !== instance.billing_contact
          )

          // Check for error in response directly since axios may not throw
          if (resp.data && resp.data.data && resp.data.data.status === 400) {
            Vue.prototype.$swal({
              title: 'Error',
              text: resp.data.data.message || 'Failed to update billing contact status.',
              type: 'error',
              confirmButtonText: 'Okay'
            })
            return false
          }

          const data = resp.data.data.data
          this.mutateMember({
            data: data
          })

          return true
        }
      })
    },
    async handleCashCallApproverStatusToggle(instance) {
      const id = instance.id;
      const name = instance.user.first_name;

      Vue.prototype.$swal({
        title: "Confirmation",
        text: `Are you sure you want to ${
          "Active" == instance.cash_call_approver ? "remove" : "designate"
        } ${name} as a Cash Call Approver?`,
        type: "warning",
        confirmButtonText: "Okay",
        preConfirm: async (confirm) => {
          if (!confirm) return false;

          const resp = await Members.toggleCashCallApproverMemberStatus(id, "Active" !== instance.cash_call_approver);
          const data = resp.data.data.data;

          this.mutateMember({
            data: data,
          });

          return true;
        },
      });
    },
    async handleRecStatusToggle(instance) {
      const id = instance.id;
      const name = instance.user.first_name;

      Vue.prototype.$swal({
        title: "Confirmation",
        text: `Are you sure you want to ${
          "Active" == instance.user_rec_status ? "deactivate" : "activate"
        } REC status for ${name}?`,
        type: "warning",
        confirmButtonText: "Okay",
        preConfirm: async (confirm) => {
          if (!confirm) return false;

          const resp = await Members.toggleRecStatus(id, "Active" !== instance.user_rec_status);
          const data = resp.data.data.data;

          this.mutateMember({
            data: data,
          });

          return true;
        },
      });
    },
  },
  created() {
    this.serviceProvider = this.memberSvc;
    this.queryParams.company_id = this.companySvc.company.id;
  },
  mounted() {
    this.$bus.trackEvent("company-role-form-success", (payload) => {
      this.memberSvc.members.results = this.memberSvc.members.results.map((item) => {
        if (payload.id === item.company.id) {
          item.company = payload;
          item.role = payload.role.role;
          item.role_value = payload.role_value;
        }

        return item;
      });
    });

    this.$bus.trackEvent("report-access-unassigned", (payload) => {
      this.memberSvc.members.results
        .filter((mitem) => {
          if ("undefined" !== typeof payload.company_id)
            return (
              "Corporate Admin" === mitem.type || mitem.company.id === payload.company_id
            );
          else return mitem.id === payload.member_id;
        })
        .forEach((mitem) => {
          const rindex = mitem.reports.findIndex(
            (ritem) => ritem.report_id === payload.report_id
          );

          if (0 <= rindex) {
            mitem.report_count -= 1;
            mitem.reports.splice(rindex, 1);

            this.memberSvc.set("member", mitem);
          }
        });
    });

    this.$bus.trackEvent("report-access-assigned", (payload) => {
      if (0 < payload.length)
        payload.forEach((pitem) => {
          this.memberSvc.members.results
            .filter((mitem) => {
              if ("undefined" !== typeof pitem.company)
                return "Corporate Admin" === mitem.type;
              else return mitem.user.id === pitem.member.user;
            })
            .forEach((mitem) => {
              mitem.report_count += 1;
              mitem.reports = mitem.reports.concat({
                report_id: pitem.report.id,
                report_assigned_id: pitem.id,
              });

              this.memberSvc.set("member", mitem);
            });
        });
    });

    this.$bus.trackEvent("datatable-cell-click", (row, column) => {
      this.memberSvc.set("member", row);

      if ("user" === column.property && "Name" === column.label)
        this.$bus.$emit("toggle-modal", {
          key: "user-name",
          active: true,
        });
      else if ("type" === column.property)
        this.$bus.$emit("toggle-modal", {
          key: "user-type",
          active: true,
        });
      else if ("report_count" === column.property)
        this.$bus.$emit("toggle-modal", {
          key: "user-report-access",
          active: true,
          id: row.id,
        });

      return false;
    });
  },
  beforeDestroy() {
    this.$bus.destroyEvents();
  },
};
</script>
